/**
 * Userpilot Initialization Hook
 *
 * This hook handles Userpilot initialization and user identification.
 * It should be called ONCE at the app root level (in App.tsx).
 *
 * For tracking events, use the `useUserpilotTracking` hook instead.
 */

import { logDebug } from '@ptek/client-logger';
import { useEffect } from 'react';
import { useWidgetAuth } from '../context/AuthContext';
import {
  identifyUser,
  initializeUserpilot,
  isEnabledForUser,
  resetUserpilot,
} from '../userpilot/internal/core';

/**
 * Initialize Userpilot and handle user identification
 *
 * This hook:
 * - Initializes Userpilot when a user authenticates
 * - Identifies the user in Userpilot with their profile data
 * - Resets Userpilot session when user logs out
 *
 * ⚠️ Only call this hook once at the app root level
 */
export const useUserpilotInit = (): void => {
  const { user, isAuthenticated } = useWidgetAuth();

  useEffect(() => {
    if (isAuthenticated && user && isEnabledForUser(user)) {
      initializeUserpilot()
        .then(() => identifyUser(user))
        .catch((error) => {
          logDebug(
            'useUserpilotInit',
            'Failed to initialize or identify user in Userpilot:',
            error
          );
        });
      return;
    }
    resetUserpilot();
  }, [isAuthenticated, user]);
};
